import React, { useEffect, useMemo, useRef, useState } from "react";

import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import { Checkbox, FormLabel, InputBase, ListItemText, makeStyles } from "@material-ui/core";
import { Search } from "lucide-react";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";

const useSelectStyles = makeStyles(() => ({
	renderValue: {
		display: "block",
		whiteSpace: "nowrap",
		overflow: "hidden",
		textOverflow: "ellipsis",
		maxWidth: "100%",
	},
	menuPaper: {
		maxHeight: "300px",
	},
	searchBox: {
		display: "flex",
		alignItems: "center",
		gap: "8px",
		padding: "8px 16px",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
	},
	searchInput: {
		flex: 1,
		fontSize: "14px",
	},
}));

const NO_QUEUE_VALUE = "__no_queue__";

const TicketsQueueSelect = ({
	userQueues,
	selectedQueueIds = [],
	onChange,
	loadSettingsQueues = false,
	onlyUserDepartments = false,
	selectAllByDefault = false,
	includeNoQueueOption = false,
	noQueueSelected = false,
	onNoQueueChange,
	label,
	className,
	allowedQueueIds = null,
}) => {
	const selectClasses = useSelectStyles();
	const [settingsQueues, setSettingsQueues] = useState([]);
	const [searchParam, setSearchParam] = useState("");
	const hasInitializedSelection = useRef(false);

	useEffect(() => {
		if (!loadSettingsQueues) {
			setSettingsQueues([]);
			return undefined;
		}

		let isMounted = true;
		const loadQueues = async () => {
			try {
				const { data } = await api.get("/queue", {
					params: {
						includeOptions: true,
						onlyUserDepartments,
					},
				});
				if (isMounted) setSettingsQueues(data);
			} catch (err) {
				if (isMounted) setSettingsQueues([]);
			}
		};

		loadQueues();
		return () => {
			isMounted = false;
		};
	}, [loadSettingsQueues, onlyUserDepartments]);

	const serviceQueues = useMemo(
		() =>
			(loadSettingsQueues ? settingsQueues : userQueues || [])
				.filter(queue => queue.isBot !== true)
				.filter(queue => {
					if (!loadSettingsQueues) return true;
					const hasSubDepartments =
						Array.isArray(queue.options) && queue.options.length > 0;
					const isSubDepartment = Number(queue.nivel) === 2;
					return !hasSubDepartments || isSubDepartment;
				})
				.filter(
					queue => allowedQueueIds === null || allowedQueueIds.includes(queue.id)
				)
				.filter(queue =>
					queue.name?.toLowerCase().includes(searchParam.toLowerCase())
				),
		[loadSettingsQueues, settingsQueues, userQueues, allowedQueueIds, searchParam]
	);

	useEffect(() => {
		if (loadSettingsQueues && settingsQueues.length === 0) return;

		const serviceQueueIds = serviceQueues.map(queue => queue.id);
		if (selectAllByDefault && !hasInitializedSelection.current) {
			hasInitializedSelection.current = true;
			onChange(serviceQueueIds);
			if (includeNoQueueOption) onNoQueueChange(true);
			return;
		}

		hasInitializedSelection.current = true;
		const validSelectedQueueIds = selectedQueueIds.filter(queueId =>
			serviceQueueIds.includes(queueId)
		);

		if (validSelectedQueueIds.length !== selectedQueueIds.length) {
			onChange(validSelectedQueueIds);
		}
	}, [
		loadSettingsQueues,
		includeNoQueueOption,
		onChange,
		onNoQueueChange,
		selectedQueueIds,
		selectAllByDefault,
		serviceQueues,
		settingsQueues,
	]);

	const handleChange = e => {
		const selectedValues = e.target.value;
		const hasNoQueue = selectedValues.includes(NO_QUEUE_VALUE);
		onChange(selectedValues.filter(value => value !== NO_QUEUE_VALUE));
		if (includeNoQueueOption) onNoQueueChange(hasNoQueue);
	};

	const handleSelectAll = e => {
		e.preventDefault();
		e.stopPropagation();
		onChange(serviceQueues.map(queue => queue.id));
		if (includeNoQueueOption) onNoQueueChange(true);
	};

	const handleClearSelection = e => {
		e.preventDefault();
		e.stopPropagation();
		onChange([]);
		if (includeNoQueueOption) onNoQueueChange(false);
	};

	const selectValue = noQueueSelected
		? [...selectedQueueIds, NO_QUEUE_VALUE]
		: selectedQueueIds;

	return (
		<div>
			<FormControl fullWidth className={className}>
				{label && <FormLabel>{label}</FormLabel>}
				<Select
					multiple
					displayEmpty
					variant="outlined"
					value={selectValue}
					onChange={handleChange}
					MenuProps={{
						anchorOrigin: {
							vertical: "bottom",
							horizontal: "left",
						},
						transformOrigin: {
							vertical: "top",
							horizontal: "left",
						},
						getContentAnchorEl: null,
						classes: { paper: selectClasses.menuPaper },
					}}
					renderValue={() => {
						const hasSelection = selectedQueueIds.length > 0 || noQueueSelected;
						if (!hasSelection) {
							return i18n.t("ticketsQueueSelect.placeholder");
						}
						const selectedNames = serviceQueues
							.filter(queue => selectedQueueIds.includes(queue.id))
							.map(queue => queue.name);
						if (noQueueSelected) {
							selectedNames.push("Sem Departamento");
						}
						const text = selectedNames.join(", ");
						return (
							<span className={selectClasses.renderValue} title={text}>
								{text}
							</span>
						);
					}}
				>
					<div
						className={selectClasses.searchBox}
						onClick={event => event.stopPropagation()}
						onKeyDown={event => event.stopPropagation()}
					>
						<Search size={16} />
						<InputBase
							className={selectClasses.searchInput}
							placeholder="Pesquisar departamento"
							value={searchParam}
							onChange={e => setSearchParam(e.target.value)}
							onClick={event => event.stopPropagation()}
							onKeyDown={event => event.stopPropagation()}
							autoFocus
						/>
					</div>
					{selectAllByDefault && (
						<>
							<MenuItem dense onClick={handleSelectAll}>
								<ListItemText primary="Marcar todos" />
							</MenuItem>
							<MenuItem dense onClick={handleClearSelection}>
								<ListItemText primary="Desmarcar todos" />
							</MenuItem>
						</>
					)}
					{includeNoQueueOption && (
						<MenuItem dense value={NO_QUEUE_VALUE}>
							<Checkbox
								size="small"
								color="primary"
								checked={noQueueSelected}
							/>
							<ListItemText primary="Sem Departamento" />
						</MenuItem>
					)}
					{serviceQueues.map(queue => (
						<MenuItem dense key={queue.id} value={queue.id}>
							<Checkbox
								style={{
									color: queue.color,
								}}
								size="small"
								color="primary"
								checked={selectedQueueIds.indexOf(queue.id) > -1}
							/>
							<ListItemText primary={queue.name} />
						</MenuItem>
					))}
				</Select>
			</FormControl>
		</div>
	);
};

export default TicketsQueueSelect;
